import { ajv } from '@cibo/core'
import { useCallback, useMemo, useState } from 'react'
import { AutoForm } from 'uniforms-mui'
import { CiboAutoFormContext } from './CiboAutoFormContext'
import { buildRequiredFieldsLookup } from './requiredFields/collectRequiredFields'

// Normal validator helper
function createValidator(schema: any) {
  const validate = ajv.compile(schema)
  return (model: any) => {
    const errors: Record<string, string> = {}
    validate(model)
    if (validate.errors) {
      for (const err of validate.errors) {
        const path = err.instancePath.slice(1) // remove leading "/"
        errors[path] = err.message || 'Error'
      }
    }
    return errors
  }
}

export function CiboAutoForm({ model, schema, warningSchema, ...props }: any) {
  const [warnings, setWarnings] = useState<Record<string, string>>({})

  const warningValidator = useMemo(() => {
    return warningSchema && createValidator(warningSchema)
  }, [warningSchema])

  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const touch = useCallback((name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }))
  }, [])

  // Compute required fields dynamically based on schema and model
  const requiredFields = useMemo(() => {
    // Handle both raw schema and JSONSchemaBridge
    const rawSchema = (schema as any)?.schema || schema

    return buildRequiredFieldsLookup(rawSchema, model)
  }, [schema, model])

  const isFieldRequired = useCallback(
    (name: string) => requiredFields.get(name) ?? false,
    [requiredFields]
  )

  return (
    <CiboAutoFormContext.Provider value={{ warnings, touch, touched, isFieldRequired }}>
      <AutoForm
        schema={schema}
        model={model}
        {...props}
        onChangeModel={newModel => {
          const warningResults = warningValidator?.(newModel)
          setWarnings(warningResults)

          props.onChangeModel?.(newModel)
        }}
      >
        {props.children}
      </AutoForm>
    </CiboAutoFormContext.Provider>
  )
}
